import type { WorkflowDefinition } from "../types";

/**
 * W18 — Decide which AI crawlers may read the site.
 *
 * `shared` and `observed`: the rules are a property-wide setting, not an
 * incident. A site that already blocks model trainers is part-way through this
 * whether or not anyone opened the playbook.
 *
 * The middle step accepts any of three shapes of rule, because "we decided"
 * looks different depending on the decision: the model-trainer switch, a block
 * list, or an allow list.
 */
export const crawlerGovernanceWorkflow: WorkflowDefinition = {
	id: "crawler-governance",
	wNumber: 18,
	title: "Decide which AI crawlers may read your site",
	outcome:
		"Choose which crawlers get your content, and check the choice is holding.",
	audiences: ["inhouse"],
	uses: ["Bot Traffic", "Traffic Control"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	isApplicable: (mode) => mode.showAnalytics,
	articleSlug: "how-to-govern-ai-crawlers-on-your-site",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "see-who-crawls",
			title: "See who is crawling the site today",
			page: "Bot Traffic",
			route: "/analytics-dashboard",
			criterion: "You've installed the plugin and traffic is arriving.",
			why: "Decide with the traffic in front of you. Blocking a name someone read in a blog post is how teams lose citations by accident.",
			docsHref: "/docs/dashboards/bot-traffic",
			tasks: [
				"Install the plugin and wait for a day of traffic",
				"Read the split between assistants, trainers, search crawlers and scrapers",
			],
			complete: { kind: "flag", key: "isVerified" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Traffic arriving",
									done: state.property.isVerified,
								},
							],
						}
					: null,
		},
		{
			id: "set-rules",
			title: "Set the rules for each kind of crawler",
			page: "Traffic Control",
			route: "/traffic-control",
			criterion: "You've set at least one rule.",
			why: "An assistant that cites you and a trainer that only takes your text are different decisions. Block the assistants and you leave the answers.",
			docsHref: "/docs/getting-started/traffic-control",
			tasks: [
				"Decide on model trainers first, since that is the big lever",
				"Allow the assistants you want citing you",
				"Add specific rules for anything else you saw in the traffic",
			],
			complete: {
				kind: "any",
				of: [
					{
						kind: "flag",
						key: "trafficControl.blockAiModelTrainers",
					},
					{
						kind: "count",
						key: "trafficControl.customBlockCount",
						gte: 1,
					},
					{
						kind: "count",
						key: "trafficControl.customAllowCount",
						gte: 1,
					},
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Model trainers blocked",
									done: state.property.trafficControl
										.blockAiModelTrainers,
								},
								{
									label: `${state.property.trafficControl.customBlockCount} blocked by name`,
									done:
										state.property.trafficControl
											.customBlockCount > 0,
								},
								{
									label: `${state.property.trafficControl.customAllowCount} allowed by name`,
									done:
										state.property.trafficControl
											.customAllowCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "watch-blocked",
			title: "Watch what the rules are actually stopping",
			page: "Bot Traffic",
			route: "/analytics-dashboard",
			criterion: "You've checked the blocked requests.",
			why: "Rules only mean something once you see them applied. An assistant you blocked by mistake shows up here first.",
			docsHref: "/docs/dashboards/bot-traffic",
			tasks: [
				"Filter to blocked requests and check who is hitting them",
				"Loosen anything you blocked by mistake",
				"Come back monthly, because new crawlers appear all the time",
			],
			complete: { kind: "manual" },
		},
	],
};
